import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
} from "@mui/material";

import {
  ArrowBackIos,
  ArrowForwardIos,
  BusinessOutlined,
  EmailOutlined,
  Inventory2Outlined,
} from "@mui/icons-material";

const HUB_EMAIL = "dh@usm.edu";

const Carousel = ({ tools = [] }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:900px)");

  const itemsToShow = isMobile ? 1 : isTablet ? 2 : 4;

  const autoSlideInterval = 5000;
  const totalItems = tools.length;

  const carouselRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const maxIndex = Math.max(totalItems - itemsToShow, 0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [totalItems, itemsToShow]);

  const nextSlide = () => {
    setCurrentIndex((previous) => (previous >= maxIndex ? 0 : previous + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((previous) => (previous <= 0 ? maxIndex : previous - 1));
  };

  useEffect(() => {
    if (totalItems <= itemsToShow) return undefined;

    const interval = window.setInterval(nextSlide, autoSlideInterval);

    return () => {
      window.clearInterval(interval);
    };
  }, [totalItems, itemsToShow, maxIndex]);

  if (!totalItems) {
    return (
      <Typography sx={{ textAlign: "center", color: "text.secondary" }}>
        No items available.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        px: {
          xs: 4.5,
          sm: 5,
          md: 6,
        },
        py: 2,
      }}
    >
      {totalItems > itemsToShow && (
        <IconButton
          onClick={prevSlide}
          aria-label="View previous equipment"
          sx={{
            position: "absolute",
            left: {
              xs: 2,
              md: 8,
            },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",

            "&:hover": {
              backgroundColor: "#fff8dc",
            },
          }}
        >
          <ArrowBackIos sx={{ fontSize: 18, ml: 0.5 }} />
        </IconButton>
      )}

      <Box
        sx={{
          overflow: "hidden",
          width: "100%",
        }}
      >
        <Box
          ref={carouselRef}
          sx={{
            display: "flex",
            transition: "transform 0.45s ease-in-out",
            transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)`,
          }}
        >
          {tools.map((tool, index) => (
            <Box
              key={`${tool.name}-${index}`}
              sx={{
                flex: `0 0 ${100 / itemsToShow}%`,
                px: {
                  xs: 0.75,
                  sm: 1,
                  md: 1.25,
                },
                boxSizing: "border-box",
              }}
            >
              <Card
                elevation={0}
                sx={{
                  height: "100%",

                  borderRadius: 3,
                  border: "1px solid #e5e7eb",
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  transition:
                    "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",

                  "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "#d4af37",
                    boxShadow: "0 14px 30px rgba(0,0,0,0.09)",
                  },
                }}
              >
                <Box
                  sx={{
                    height: 205,
                    px: 2,
                    py: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#fafafa",
                    borderBottom: "1px solid #eeeeee",
                  }}
                >
                  {tool.image ? (
                    <CardMedia
                      component="img"
                      image={tool.image}
                      alt={tool.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Inventory2Outlined
                      sx={{
                        fontSize: 64,
                        color: "#9ca3af",
                      }}
                    />
                  )}
                </Box>

                <CardContent
                  sx={{
                    p: 2.25,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,

                    "&:last-child": {
                      pb: 2.25,
                    },
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      color: "#111827",
                      fontWeight: 700,
                      fontSize: {
                        xs: "1rem",
                        md: "1.05rem",
                      },
                      lineHeight: 1.4,
                    }}
                  >
                    {tool.name}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1,
                      color: "#5f6368",
                      fontSize: "0.88rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {tool.description}
                  </Typography>

                  <Box
                    sx={{
                      mt: 1.5,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.75,
                    }}
                  >
                    <Chip
                      icon={<Inventory2Outlined />}
                      label={`${tool.units} ${tool.units === 1 ? "unit" : "units"}`}
                      size="small"
                      sx={{
                        backgroundColor: "#fff8dc",
                        color: "#7c5b00",
                        border: "1px solid #ead083",
                        fontWeight: 700,
                      }}
                    />

                    <Chip
                      icon={<BusinessOutlined />}
                      label={tool.location}
                      size="small"
                      variant="outlined"
                      sx={{
                        maxWidth: "100%",
                        color: "#4b5563",
                        borderColor: "#d1d5db",
                      }}
                    />
                  </Box>
                  {tool.accessType === "hub" ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Link
                        href={`mailto:${HUB_EMAIL}?subject=${encodeURIComponent(
                          `Equipment Access Request: ${tool.name}`
                        )}`}
                        underline="hover"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.6,
                          color: "#8a6211",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                        }}
                      >
                        <EmailOutlined sx={{ fontSize: 16 }} />
                        Request access
                      </Link>
                    </Box>
                  ) : tool.accessType === "cdh-lab" ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        sx={{
                          color: "#6b7280",
                          fontSize: "0.8rem",
                          lineHeight: 1.45,
                          mb: 0.7,
                        }}
                      >
                        Available in the Center for Digital Humanities Lab.
                      </Typography>

                      <Link
                        href={`mailto:${HUB_EMAIL}?subject=${encodeURIComponent(
                          `CDH Lab Software Access Request: ${tool.name}`
                        )}`}
                        underline="hover"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.6,
                          color: "#8a6211",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                        }}
                      >
                        <EmailOutlined sx={{ fontSize: 16 }} />
                        Ask about lab access
                      </Link>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        sx={{
                          color: "#6b7280",
                          fontSize: "0.8rem",
                          lineHeight: 1.45,
                          mb: 0.7,
                        }}
                      >
                        Available through the DigiLab at Cook Library.
                      </Typography>

                      <Link
                        href={`mailto:${HUB_EMAIL}?subject=${encodeURIComponent(
                          `DigiLab Equipment Access Request: ${tool.name}`
                        )}`}
                        underline="hover"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.6,
                          color: "#8a6211",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                        }}
                      >
                        <EmailOutlined sx={{ fontSize: 16 }} />
                        Ask the Hub to coordinate
                      </Link>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>

      {totalItems > itemsToShow && (
        <IconButton
          onClick={nextSlide}
          aria-label="View next equipment"
          sx={{
            position: "absolute",
            right: {
              xs: 2,
              md: 8,
            },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",

            "&:hover": {
              backgroundColor: "#fff8dc",
            },
          }}
        >
          <ArrowForwardIos sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default Carousel;
